import api from "../../utils/api";

export const REVIEWS_ENDPOINTS = {
  list: (locationId) => `/api/locations/${locationId}/reviews/`,
  stats: (locationId) => `/api/locations/${locationId}/stats/`,
  sync: (locationId) => `/api/locations/${locationId}/reviews/sync/`,
  incrementalSync: (locationId) =>
    `/api/locations/${locationId}/reviews/incremental-sync/`,
  detail: (reviewId) => `/api/reviews/${reviewId}/`,
  reply: (reviewId) => `/api/reviews/${reviewId}/reply/`,
  refresh: (reviewId) => `/api/reviews/${reviewId}/refresh/`,
};

const asArray = (value) => (Array.isArray(value) ? value : []);

export const normalizeReview = (raw) => ({
  id: raw.id,
  locationId: raw.location,
  platform: raw.platform || "google",
  author: raw.review_author || "Anonym",
  authorPhoto: raw.review_author_photo || null,
  isAnonymous: Boolean(raw.isAnonymous),
  rating: Number(raw.rating) || 0,
  text: raw.review_text || "",
  createTime: raw.create_time,
  updateTime: raw.update_time,
  reply: raw.reply
    ? {
        id: raw.reply.id,
        comment: raw.reply.comment,
        createTime: raw.reply.create_time,
        state: raw.reply.state,
      }
    : null,
  deletionCase: raw.deletion_case
    ? {
        caseId: raw.deletion_case.case_id,
        caseReference: raw.deletion_case.case_reference,
        caseStatus: raw.deletion_case.case_status,
        deletionOutcome: raw.deletion_case.deletion_outcome,
      }
    : null,
});

export const normalizeStats = (raw) => ({
  locationId: raw.location_id,
  totalReviews: raw.total_reviews ?? 0,
  averageRating: raw.average_rating ?? 0,
  newToday: raw.new_today ?? 0,
  negative: raw.negative ?? 0,
  sentiment: raw.sentiment
    ? {
        positive: raw.sentiment.positive ?? 0,
        neutral: raw.sentiment.neutral ?? 0,
        negative: raw.sentiment.negative ?? 0,
        positivePercent: raw.sentiment.positive_percent ?? 0,
        neutralPercent: raw.sentiment.neutral_percent ?? 0,
        negativePercent: raw.sentiment.negative_percent ?? 0,
      }
    : null,
});

/**
 * @param {number} locationId
 * @param {{ page?: number; pageSize?: number; rating?: number; ordering?: string }} [opts]
 */
export async function fetchLocationReviews(locationId, opts = {}) {
  const params = new URLSearchParams();
  if (opts.page) params.set("page", String(opts.page));
  if (opts.pageSize) params.set("page_size", String(opts.pageSize));
  if (opts.rating != null) params.set("rating", String(opts.rating));
  if (opts.ordering) params.set("ordering", opts.ordering);

  const qs = params.toString();
  const path = `${REVIEWS_ENDPOINTS.list(locationId)}${qs ? `?${qs}` : ""}`;
  const data = await api.get(path, { auth: true });

  const results = asArray(data?.results ?? data);
  return {
    reviews: results.map(normalizeReview),
    count: data?.count ?? results.length,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  };
}

export async function fetchLocationStats(locationId) {
  const data = await api.get(REVIEWS_ENDPOINTS.stats(locationId), { auth: true });
  return normalizeStats(data);
}

export async function syncLocationReviews(locationId, { incremental = true } = {}) {
  const path = incremental
    ? REVIEWS_ENDPOINTS.incrementalSync(locationId)
    : REVIEWS_ENDPOINTS.sync(locationId);
  return api.post(path, {}, { auth: true });
}

export async function postReviewReply(reviewId, comment) {
  return api.post(
    REVIEWS_ENDPOINTS.reply(reviewId),
    { comment },
    { auth: true }
  );
}

export async function fetchReviewDetail(reviewId) {
  const data = await api.get(REVIEWS_ENDPOINTS.detail(reviewId), { auth: true });
  return normalizeReview(data);
}

/** Fetch 1–3 star reviews for a connected location (critical / attention). */
export async function fetchCriticalReviews(locationId, { pageSize = 200 } = {}) {
  const [oneStar, twoStar, threeStar] = await Promise.all([
    fetchLocationReviews(locationId, { rating: 1, page: 1, pageSize }),
    fetchLocationReviews(locationId, { rating: 2, page: 1, pageSize }),
    fetchLocationReviews(locationId, { rating: 3, page: 1, pageSize }),
  ]);

  const merged = [...oneStar.reviews, ...twoStar.reviews, ...threeStar.reviews];
  merged.sort((a, b) => {
    const ta = new Date(a.createTime || 0).getTime();
    const tb = new Date(b.createTime || 0).getTime();
    return tb - ta;
  });

  return merged;
}

/** Rating distribution for sentiment visualization (prefer location stats sentiment when available). */
export async function fetchRatingDistribution(locationId) {
  const stats = await fetchLocationStats(locationId);
  if (stats.sentiment) {
    return {
      positive: stats.sentiment.positivePercent,
      neutral: stats.sentiment.neutralPercent,
      negative: stats.sentiment.negativePercent,
      counts: stats.sentiment,
    };
  }

  const ratings = [1, 2, 3, 4, 5];
  const results = await Promise.all(
    ratings.map((rating) =>
      fetchLocationReviews(locationId, { rating, page: 1, pageSize: 1 })
    )
  );

  const counts = results.map((result) => result.count);
  const total = counts.reduce((sum, count) => sum + count, 0) || 1;

  return {
    positive: ((counts[3] + counts[4]) / total) * 100,
    neutral: (counts[2] / total) * 100,
    negative: ((counts[0] + counts[1]) / total) * 100,
  };
}
